import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  useGetDevices,
  useGetRoadmapCards,
  useGetRoadmapStages,
  useUpdateAdHocTask,
} from "../../hooks";
import type { Device } from "../../stores/models";
import type { RawEditorHandle } from "../DocViewer/components/markdownDocViewer/rawEditor/RawEditor";
import RawEditor from "../DocViewer/components/markdownDocViewer/rawEditor/RawEditor";
import { useSnackbar } from "../Snackbar";

type CardOption = {
  cardKey: string;
  title: string;
  stageTitle: string;
};

type EditableTask = {
  taskKey: string;
  title: string;
  description: string | null;
  parentCardKey?: string | null;
  device: {
    id: string;
    name: string;
  } | null;
};

type Props = {
  open: boolean;
  onClose: () => void;
  orgId: string;
  task: EditableTask;
};

export const EditTaskModal = ({ open, onClose, orgId, task }: Props) => {
  const { showSnackbar } = useSnackbar();
  const updateAdHocTask = useUpdateAdHocTask({ orgId });
  const [title, setTitle] = useState(task.title);
  const [description, setDescription] = useState(task.description ?? "");
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
  const [selectedCard, setSelectedCard] = useState<CardOption | null>(null);
  const [error, setError] = useState<string | null>(null);
  const descriptionEditorRef = useRef<RawEditorHandle | null>(null);

  const { data: devices, isLoading: isLoadingDevices } = useGetDevices({
    orgId,
  });

  const firstDeviceId = devices?.[0]?.id ?? "";
  const { data: cards } = useGetRoadmapCards({
    orgId,
    deviceId: firstDeviceId,
    enabled: !!firstDeviceId,
  });
  const { data: stages } = useGetRoadmapStages({
    orgId,
    deviceId: firstDeviceId,
    enabled: !!firstDeviceId,
  });

  const cardOptions = useMemo(() => {
    if (!cards || !stages) return [];
    return cards
      .filter((card) => card.enabled)
      .map((card) => {
        const stage = stages.find((s) => s.stageKey === card.parentStageKey);
        return {
          cardKey: card.cardKey,
          title: card.title,
          stageTitle: stage?.title ?? "Unknown Stage",
        };
      })
      .sort((a, b) => {
        const stageCompare = a.stageTitle.localeCompare(b.stageTitle);
        if (stageCompare !== 0) return stageCompare;
        return a.title.localeCompare(b.title);
      });
  }, [cards, stages]);

  useEffect(() => {
    if (!open) return;
    setTitle(task.title);
    setDescription(task.description ?? "");
    descriptionEditorRef.current?.reloadContent(task.description ?? "");
    setError(null);
  }, [open, task.description, task.title]);

  useEffect(() => {
    if (!open || !devices) return;
    const nextDevice = task.device?.id
      ? devices.find((device) => device.id === task.device?.id) ?? null
      : null;
    setSelectedDevice(nextDevice);
  }, [devices, open, task.device?.id]);

  useEffect(() => {
    if (!open) return;
    const nextCard = task.parentCardKey
      ? cardOptions.find((cardOption) => cardOption.cardKey === task.parentCardKey) ??
        null
      : null;
    setSelectedCard(nextCard);
  }, [cardOptions, open, task.parentCardKey]);

  const hasMultipleDevices = devices && devices.length > 1;

  const handleSubmit = async () => {
    if (!title.trim()) {
      setError("Title is required");
      return;
    }

    setError(null);
    const descriptionMarkdown =
      descriptionEditorRef.current?.getMarkdownContent() ?? description;

    try {
      await updateAdHocTask.mutateAsync({
        taskKey: task.taskKey,
        data: {
          title: title.trim(),
          description: descriptionMarkdown.trim() || null,
          deviceId: selectedDevice?.id ?? null,
          parentCardKey: selectedCard?.cardKey ?? null,
        },
      });
      onClose();
    } catch (_err) {
      const errorMessage = "Failed to update task. Please try again.";
      setError(errorMessage);
      showSnackbar(errorMessage);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Task</DialogTitle>
      <DialogContent>
        <div className="flex flex-col gap-4 pt-2">
          <TextField
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            fullWidth
            required
            error={!!error && !title.trim()}
            helperText={error && !title.trim() ? error : ""}
          />

          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              className="mb-1 block"
            >
              Description
            </Typography>
            <RawEditor
              ref={descriptionEditorRef}
              markdown={description}
              onChange={setDescription}
              autoFocus={false}
            />
          </Box>

          {cardOptions.length > 0 && (
            <Autocomplete
              options={cardOptions}
              getOptionLabel={(option) => option.title}
              groupBy={(option) => option.stageTitle}
              value={selectedCard}
              onChange={(_, newValue) => setSelectedCard(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Roadmap Card (optional)"
                  helperText="Link this task to a roadmap card"
                />
              )}
              isOptionEqualToValue={(option, value) =>
                option.cardKey === value?.cardKey
              }
            />
          )}

          {hasMultipleDevices && (
            <Autocomplete
              options={devices ?? []}
              getOptionLabel={(device) => device.name}
              value={selectedDevice}
              onChange={(_, newValue) => {
                setSelectedDevice(newValue);
              }}
              loading={isLoadingDevices}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Device (optional)"
                  helperText="Set a specific device scope for this task"
                />
              )}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
            />
          )}
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={updateAdHocTask.isPending}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={updateAdHocTask.isPending}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};
